import { localDay, type ListPricing } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { carrierLabel, fmt } from '../../format';
import { useSession } from '../../session';
import { Empty, ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { TariffTable } from '../../ui/tariff';
import { KIND_LABEL, PricingEditor, TierTable, draftFrom, pricingPayload, type PricingDraft } from './Pricing';

const today = () => localDay(new Date());

/**
 * Yönetim → Fiyat listesi: the list price, what every seller without prices of its own pays (the
 * website's prices). Versioned by its start day like a seller's own prices: a change starts today or
 * later, the version in force ends the day before, a version a charge was priced by is never rewritten.
 * The depo yöneticisi changes it; the vardiya sorumlusu reads it.
 */
export function ListPrice() {
  const { isAdmin } = useSession();
  const toast = useToast();
  const [on, setOn] = useState(today);
  const list = useLoad(() => api<ListPricing>('GET', `/api/admin/pricing/list?on=${on}`), [on]);
  const [editing, setEditing] = useState<PricingDraft | null>(null);
  /** the day whose list price the editor was filled from */
  const [copiedFrom, setCopiedFrom] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const open = async () => {
    if (!list.data) return;
    setError(null);
    // a change starts today or later: it is filled from the list price of that day, not from a past
    // version being looked at (a later day can be chosen in the editor)
    const from = on > today() ? on : today();
    try {
      const base = from === list.data.on ? list.data : await api<ListPricing>('GET', `/api/admin/pricing/list?on=${from}`);
      setCopiedFrom(base.tariff || base.rateCards.length > 0 ? from : null);
      setEditing({ ...draftFrom(base.rateCards, base.tariff, ''), validFrom: from });
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };
  const save = async () => {
    if (!editing) return;
    const payload = pricingPayload(editing);
    if ('problem' in payload) return setError(payload.problem);
    const dropped = list.data ? laterVersions(list.data, editing.validFrom) : [];
    if (dropped.length > 0 && !window.confirm(`İleri tarihli sürüm silinir: ${dropped.map((v) => fmt.date(v.validFrom)).join(', ')}. Yeni fiyat süresiz geçerli olur. Kaydedilsin mi?`)) return;
    setBusy(true);
    try {
      await api('PUT', '/api/admin/pricing/list', payload.body);
      toast(`Liste fiyatı ${fmt.date(editing.validFrom)} itibarıyla geçerli`, 'ok');
      setEditing(null);
      setOn(editing.validFrom > today() ? editing.validFrom : today());
      void list.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (list.error) return <ErrorBanner message={list.error} onRetry={() => void list.reload()} />;
  if (!list.data) return <Spinner />;
  const p = list.data;
  const since = [p.tariff?.validFrom, ...p.rateCards.map((c) => c.validFrom)].filter((d): d is string => !!d).sort().at(-1);
  const empty = !p.tariff && p.rateCards.length === 0;

  return (
    <>
      <section className="card">
        <div className="card-h">
          <Icon name="receipt" />
          <h2>Liste fiyatı</h2>
          <span className="sub">Kendi fiyatı olmayan her satıcıya uygulanır; tutarlar KDV hariç</span>
          <span className="spacer" style={{ flex: 1 }} />
          {isAdmin && (
            <button className="btn primary" onClick={() => void open()}>
              <Icon name="edit" size={16} /> {empty ? 'Liste fiyatını tanımla' : 'Liste fiyatını değiştir'}
            </button>
          )}
        </div>
        <div className="card-b row" style={{ gap: 12 }}>
          <label className="row" style={{ gap: 6 }}>
            <span className="muted">Geçerli olduğu gün</span>
            <input className="input" type="date" style={{ width: 'auto' }} value={on} onChange={(e) => e.target.value && setOn(e.target.value)} />
          </label>
          {on !== today() && (
            <button className="btn sm ghost" onClick={() => setOn(today())}>
              Bugün
            </button>
          )}
          {since && <span className="chip">{fmt.date(since)} tarihinden beri</span>}
          {p.upcomingChanges > 0 && <span className="chip warn">İleri tarihli değişiklik var</span>}
        </div>
      </section>

      {empty ? (
        <section className="card">
          <Empty title={`${fmt.date(on)} günü geçerli liste fiyatı yok`}>
            Liste fiyatı yokken kendi fiyatı olmayan satıcılardan hiçbir ücret alınmaz: siparişler, depolama, koli ve iadeler ücretsiz kalır.{' '}
            {isAdmin ? '“Liste fiyatını tanımla” ile girin.' : 'Depo yöneticisi tanımlamalı.'} Sitedeki fiyatlar sunucudan da yüklenebilir (pricing:set-list).
          </Empty>
        </section>
      ) : (
        <div className="grid">
          {p.tariff && (
            <section className="card wide">
              <div className="card-h">
                <h2>Sabit ücretler</h2>
                <span className="sub">
                  {fmt.date(p.tariff.validFrom)} – {p.tariff.validTo ? fmt.date(p.tariff.validTo) : 'süresiz'}
                </span>
              </div>
              <div className="table-wrap">
                <TariffTable tariff={p.tariff} on={on} />
              </div>
            </section>
          )}
          {p.rateCards.map((c) => (
            <section key={c.id} className="card">
              <div className="card-h">
                <span className="chip">{KIND_LABEL[c.kind]}</span>
                <h2>{c.kind === 'shipping' ? carrierLabel(c.carrierCode) : 'Paket başı elleçleme'}</h2>
                <span className="sub">{c.name}</span>
              </div>
              <div className="card-b">
                <TierTable card={c} />
              </div>
            </section>
          ))}
          {p.rateCards.length === 0 && (
            <section className="card wide">
              <div className="card-b muted" style={{ fontSize: 13.5 }}>
                Barem yok: elleçlemede desi baremi uygulanmaz; kargo liste fiyatında yok (sitede gösterilmez, ayrıca faturalanır). Kargo baremi gerekirse
                değiştirirken ekleyin ya da satıcının kendi fiyatına koyun.
              </div>
            </section>
          )}
        </div>
      )}

      {p.versions.length > 0 && (
        <section className="card">
          <div className="card-h">
            <h2>Sürümler</h2>
            <span className="sub">Bir sürümle ücret işlendiyse o sürüm değiştirilmez; değişiklik bugünden ya da ileri bir günden başlar</span>
          </div>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Başlangıç</th>
                  <th>Bitiş</th>
                  <th>Durum</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {p.versions.map((v) => (
                  <tr key={v.validFrom}>
                    <td className="nowrap">{fmt.date(v.validFrom)}</td>
                    <td className="nowrap">{v.validTo ? fmt.date(v.validTo) : 'süresiz'}</td>
                    <td>
                      {v.validFrom > today() ? <span className="chip warn">İleri tarihli</span> : v.used ? <span className="chip ok">Ücret işlendi</span> : <span className="chip">Henüz ücret işlenmedi</span>}
                    </td>
                    <td className="num">
                      <button className="btn sm ghost" onClick={() => setOn(v.validFrom)}>
                        <Icon name="eye" size={16} /> Göster
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <div className="card-b muted" style={{ fontSize: 13.5 }}>
          {p.customSellers.length === 0
            ? 'Bütün satıcılar liste fiyatında.'
            : `Kendi fiyatı olan satıcılar: ${p.customSellers.join(', ')}. Onlarda kendi tanımladıkları barem ya da sabit ücretler geçerlidir (Müşteriler → firma → Fiyatlandırma).`}
        </div>
      </section>

      {editing && (
        <Modal
          wide
          title="Liste fiyatı"
          onClose={() => setEditing(null)}
          footer={
            <>
              <button className="btn" onClick={() => setEditing(null)}>
                Vazgeç
              </button>
              <button className="btn primary" disabled={busy} onClick={() => void save()}>
                Kaydet
              </button>
            </>
          }
        >
          {error && <div className="banner bad" style={{ marginBottom: 12 }}>{error}</div>}
          <p className="muted" style={{ marginTop: 0, fontSize: 13.5 }}>
            Yeni fiyat seçtiğiniz günden başlar; şimdiki sürüm bir gün önce biter ve o güne kadar işlenen ücretler değişmez. Liste fiyatındaki bütün satıcıları
            etkiler.{copiedFrom && ` Değerler ${fmt.date(copiedFrom)} günü geçerli liste fiyatından alındı.`}
          </p>
          <ReplacedVersions pricing={p} validFrom={editing.validFrom} />
          <PricingEditor draft={editing} onChange={setEditing} list />
        </Modal>
      )}
    </>
  );
}

/** The versions that start after `validFrom`: a change from that day deletes them (they were never used). */
const laterVersions = (p: ListPricing, validFrom: string) => p.versions.filter((v) => v.validFrom > validFrom);

/** What saving from `validFrom` does to the versions that start on or after it, said before it is done. */
function ReplacedVersions({ pricing, validFrom }: { pricing: ListPricing; validFrom: string }) {
  const later = laterVersions(pricing, validFrom);
  const same = pricing.versions.some((v) => v.validFrom === validFrom);
  if (later.length === 0 && !same) return null;
  return (
    <div className="banner warn" style={{ marginBottom: 12 }}>
      {same && `${fmt.date(validFrom)} günü başlayan sürümün yerine geçer. `}
      {later.length > 0 &&
        `İleri tarihli sürüm silinir: ${later.map((v) => fmt.date(v.validFrom)).join(', ')}. Yeni fiyat süresiz geçerli olur; ileri tarihli değişikliği yeniden girmeniz gerekir.`}
    </div>
  );
}
